import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AvailabilityGrid } from "@/components/AvailabilityGrid";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Loader2, Save, Check, X, CalendarClock, Briefcase, GraduationCap, Phone, Star,
} from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    requested: "bg-amber-100 text-amber-700",
    confirmed: "bg-green-100 text-green-700",
    declined: "bg-red-100 text-red-700",
  };
  return <span className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${map[status] || "bg-secondary text-muted-foreground"}`}>{status}</span>;
};

const ExpertPortal = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [expert, setExpert] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_expert)) navigate("/");
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    try {
      const [m, b] = await Promise.all([api.get("/expert/me"), api.get("/expert/bookings")]);
      setExpert(m.data);
      setAvailability(m.data.availability || {});
      setBookings(b.data);
    } catch (e) {
      toast.error("Could not load your expert profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user?.is_expert) load(); }, [user, load]);

  const saveAvailability = async () => {
    setSaving(true);
    try {
      const res = await api.put("/expert/availability", { availability });
      setAvailability(res.data.availability || {});
      toast.success("Availability updated");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/expert/bookings/${id}/status`, { status });
      toast.success(status === "confirmed" ? "Confirmed — client notified on WhatsApp" : `Booking ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (authLoading || loading || !user?.is_expert) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const pending = bookings.filter((b) => b.status === "requested");

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 font-body" data-testid="expert-portal">
      {/* Header */}
      <Card className="p-6 flex flex-col sm:flex-row gap-5 items-start sm:items-center">
        <Avatar className="h-20 w-20 border-2 border-border">
          <AvatarImage src={expert.image} alt={expert.name} className="object-cover" />
          <AvatarFallback>{expert.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl tracking-tight">{expert.name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5"><Briefcase className="h-3.5 w-3.5" /> {expert.current_designation}{expert.company ? ` · ${expert.company}` : ""}</p>
          {expert.education && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> {expert.education}</p>}
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-accent fill-accent" /> {expert.rating}</span>
            {expert.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {expert.phone}</span>}
            <span className="text-accent font-semibold">{expert.rate}</span>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-5 gap-6 mt-6">
        {/* Availability editor */}
        <Card className="lg:col-span-3 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /> Your availability</h2>
            <Button onClick={saveAvailability} disabled={saving} size="sm" className="rounded-full" data-testid="save-availability-btn">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          <AvailabilityGrid value={availability} onChange={setAvailability} testidPrefix="portal-avail" />
        </Card>

        {/* Bookings */}
        <Card className="lg:col-span-2 p-6">
          <h2 className="font-heading font-semibold text-lg mb-4">Your bookings {pending.length > 0 && <span className="text-xs font-normal text-amber-600">({pending.length} pending)</span>}</h2>
          {bookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No booking requests yet.</p>
          ) : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {bookings.map((b) => (
                <div key={b.id} className="border border-border rounded-xl p-3" data-testid={`portal-booking-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{b.customer_name}</p>
                    <StatusBadge status={b.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{b.customer_designation || "—"}{b.customer_company ? ` · ${b.customer_company}` : ""}</p>
                  <p className="text-xs mt-1 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {b.date} · {b.time_slot}</p>
                  <p className="text-xs mt-1 flex items-center gap-1"><Phone className="h-3 w-3" /> {b.customer_phone}</p>
                  {b.message && <p className="text-xs text-muted-foreground mt-1 italic">"{b.message}"</p>}
                  {b.status === "requested" && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="rounded-full h-7 flex-1" onClick={() => setStatus(b.id, "confirmed")} data-testid={`portal-confirm-${b.id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Accept
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-7" onClick={() => setStatus(b.id, "declined")} data-testid={`portal-decline-${b.id}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ExpertPortal;
